import { themeAtom } from "../../shared/atoms";
import Datepicker from "../../shared/components/Datepicker/Datepicker";
import { classificationSociety } from "../const/classification-society.const";
import { Bq1GeneralInformationM } from "../models/bq1-general-information.model";
import { formDate } from "../services/baltic-forms.utils";
import { useAtomValue } from "jotai";
import React from "react";

export function Bq1Classification(
  form: Bq1GeneralInformationM,
  updateForm: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void,
  updateDate: (date: Date | null, field: string) => void,
  toggleComments: (inputId: string) => void,
  isCommentsVisible: (inputId: string) => boolean,
  readonly: boolean,
) {
  const theme = useAtomValue(themeAtom);

  return (
    <div>
      <h2 className="mb-2 mt-[120px] text-lg font-semibold">Classification</h2>
      <br />
      <div className="mb-8 grid grid-cols-4 gap-4">
        {/* Classification society classificationSociety*/}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Classification society</label>
          <select
            disabled={readonly}
            name="classificationSociety"
            value={form.classificationSociety}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          >
            <option value=""></option>
            {classificationSociety.map((cs) => (
              <option key={cs} value={cs}>
                {cs}
              </option>
            ))}
          </select>
        </div>

        {/* Class Notation */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Class Notation</label>
          <textarea
            disabled={readonly}
            name="classNotation"
            value={form.classNotation}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          />
        </div>
      </div>
      {/* Previous classification */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        {/* previousClassificationSociety */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">
            If Classification society changed, what was the previous Classification Society?
          </label>
          <select
            disabled={readonly}
            name="previousClassificationSociety"
            value={form.previousClassificationSociety}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          >
            <option value=""></option>
            {classificationSociety.map((cs) => (
              <option key={cs} value={cs}>
                {cs}
              </option>
            ))}
          </select>
        </div>

        {/* Date Classification Society Changed */}
        <div className="col-span-1">
          <div className="col-span-1">
            <label className="block text-sm font-bold">
              Date of change
              <br />
              &nbsp;
            </label>
            <Datepicker
              selected={formDate(form.dateClassificationSocietyChanged)}
              onChange={(date) => updateDate(date, "dateClassificationSocietyChanged")}
            />
          </div>
        </div>
      </div>

      <div className="mb-8 grid grid-cols-4 gap-4">
        {/* Date of last dry dock */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Date of last dry dock</label>
          <Datepicker
            disabled={readonly}
            selected={formDate(form.dateOfLastDryDock)}
            onChange={(date) => updateDate(date, "dateOfLastDryDock")}
          />
        </div>

        {/* Place of last dry-dock */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Place of last dry-dock</label>
          <input
            disabled={readonly}
            type="text"
            name="placeOfLastDryDock"
            value={form.placeOfLastDryDock}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          />
        </div>
      </div>

      {/* Date next dry dock due */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Date next dry dock due</label>
          <Datepicker
            disabled={readonly}
            selected={formDate(form.dateNextDryDockDue)}
            onChange={(date) => updateDate(date, "dateNextDryDockDue")}
          />
        </div>
      </div>

      <div className="mb-8 grid grid-cols-4 gap-4">
        {/* Date of last special survey */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Date of last special survey</label>
          <Datepicker
            disabled={readonly}
            selected={formDate(form.dateOfLastSpecialSurvey)}
            onChange={(date) => updateDate(date, "dateOfLastSpecialSurvey")}
          />
        </div>

        {/* Date next special survey due */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Date next special survey due</label>
          <Datepicker
            disabled={readonly}
            selected={formDate(form.dateNextSpecialSurveyDue)}
            onChange={(date) => updateDate(date, "dateNextSpecialSurveyDue")}
          />
        </div>
      </div>

      <div className="mb-8 grid grid-cols-4 gap-4">
        {/* Certificate of Class (COC) (Last Annual) */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Certificate of Class (COC) (Last Annual)</label>
          <Datepicker
            disabled={readonly}
            selected={formDate(form.certificateOfClassLastAnnual)}
            onChange={(date) => updateDate(date, "certificateOfClassLastAnnual")}
          />
        </div>

        {/* Certificate of Class (COC) (Next Annual) */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Certificate of Class (COC) (Next Annual)</label>
          <Datepicker
            disabled={readonly}
            selected={formDate(form.certificateOfClassNextAnnual)}
            onChange={(date) => updateDate(date, "certificateOfClassNextAnnual")}
          />
        </div>
      </div>

      {/* Enhanced Survey Program */}
      <div className="mb-4">
        <label className="block text-sm font-bold">Does the vessel have Enhanced Survey Program (ESP)?</label>
        <div className="mt-1 flex space-x-4">
          {["yes", "no", "n/a"].map((val) => (
            <label key={val} className="inline-flex items-center space-x-1">
              <input
                disabled={readonly}
                type="radio"
                name="doesVesselHaveEsp"
                value={val}
                checked={form.doesVesselHaveEsp === val}
                onChange={updateForm}
                className="form-radio"
              />
              <span className="text-sm capitalize">{val}</span>
            </label>
          ))}
        </div>
        <label className="block text-sm font-bold">
          <button
            className={theme.balticQuestionnaire.blueText}
            onClick={() => toggleComments("doesVesselHaveEspComments")}
          >
            Comments
          </button>
        </label>
        {(form.doesVesselHaveEspComments || isCommentsVisible("doesVesselHaveEspComments")) && (
          <textarea
            disabled={readonly}
            name="doesVesselHaveEspComments"
            value={form.doesVesselHaveEspComments}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          />
        )}
      </div>

      {/* Does vessel comply with IACS #1 cargo hold? */}
      <div className="mb-4">
        <label className="block text-sm font-bold">
          Does vessel comply with IACS unified requirements regarding number 1 cargo hold and double bottom tank steel
          structure?
        </label>
        <div className="mt-1 flex space-x-4">
          {["yes", "no", "n/a"].map((val) => (
            <label key={val} className="inline-flex items-center space-x-1">
              <input
                disabled={readonly}
                type="radio"
                name="doesVesselComplyWithIacsNo1Cargo"
                value={val}
                checked={form.doesVesselComplyWithIacsNo1Cargo === val}
                onChange={updateForm}
                className="form-radio"
              />
              <span className="text-sm capitalize">{val}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Has classification society verified compliance? */}
      <div className="mb-6">
        <label className="block text-sm font-bold">
          Has classification society verified compliance with IACS unified requirements regarding number 1 cargo hold
          and double bottom tank steel structure?:
        </label>
        <div className="mt-1 flex space-x-4">
          {["yes", "no", "n/a"].map((val) => (
            <label key={val} className="inline-flex items-center space-x-1">
              <input
                disabled={readonly}
                type="radio"
                name="hasClassSocietyVerifiedIacsNo1Cargo"
                value={val}
                checked={form.hasClassSocietyVerifiedIacsNo1Cargo === val}
                onChange={updateForm}
                className="form-radio"
              />
              <span className="text-sm capitalize">{val}</span>
            </label>
          ))}
        </div>
        <label className="block text-sm font-bold">
          <button
            className={theme.balticQuestionnaire.blueText}
            onClick={() => toggleComments("hasClassSocietyVerifiedIacsNo1CargoComments")}
          >
            Comments
          </button>
        </label>
        {(form.hasClassSocietyVerifiedIacsNo1CargoComments ||
          isCommentsVisible("hasClassSocietyVerifiedIacsNo1CargoComments")) && (
          <textarea
            disabled={readonly}
            name="hasClassSocietyVerifiedIacsNo1CargoComments"
            value={form.hasClassSocietyVerifiedIacsNo1CargoComments}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          />
        )}
      </div>
    </div>
  );
}
